import React, { useState, useEffect } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { Helmet } from "react-helmet"; // or 'react-helmet-async'
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL, DEBUG_ACTIVE } from "../../../api";

export default function BlogPage() {
  const { id } = useParams(); // Grab the "id" from the URL
  const history = useNavigate(); // For programmatic navigation
  const [loadingContent, setLoadingContent] = useState(false);
  const [blogData, setBlogData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch the blog post data whenever the "id" changes
  useEffect(() => {
    async function loadBlogPost() {
      setLoadingContent(true);

      try {
        const res = await axios.get(`${API_BASE_URL}/blog/posts/${id}`);
        setBlogData(res.data);
        setError(null);
      } catch (err) {
        if (DEBUG_ACTIVE) {
          console.error("Error fetching blog post:", err);
        }
        setError("Error loading blog post");
        setBlogData(null);
      } finally {
        setLoadingContent(false);
      }
    }

    loadBlogPost();
  }, [id]);

  // Handle loading state
  if (loadingContent) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <h1 className="text-2xl text-red-600">Error loading blog post</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  // Handle case where no blog data is found
  if (!blogData) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <h2 className="text-2xl text-gray-800">Blog post not found</h2>
        <button
          onClick={() => history.push("/blog")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Return to Blog
        </button>
      </div>
    );
  }

  // Render the blog post
  return (
    <div className="container mx-auto px-4 py-10">
      {/* Helmet for setting <head> metadata in React projects */}
      <Helmet>
        <title>{blogData.header || "Blog Post"}</title>
        <meta
          name="description"
          content={blogData.p1 || "Read our latest blog post"}
        />
        <meta property="og:title" content={blogData.header || "Blog Post"} />
        <meta
          property="og:description"
          content={blogData.p1 || "Read our latest blog post"}
        />
      </Helmet>

      <article className="prose lg:prose-xl mx-auto">
        <h1 className="mb-4 text-4xl font-bold text-gray-800">
          {blogData.header}
        </h1>
        {blogData.p1 && (
          <p className="mb-6 text-lg text-gray-700">{blogData.p1}</p>
        )}

        {/* Render additional content if available */}
        {Array.isArray(blogData.content) &&
          blogData.content.map((value, index) => (
            <div key={index}>
              {value.heading && (
                <h2 className="mt-10 mb-4 text-2xl font-semibold text-gray-800">
                  {value.heading}
                </h2>
              )}
              {value.paragraph && (
                <p className="mb-6 text-lg text-gray-700">{value.paragraph}</p>
              )}
            </div>
          ))}

        {/* Display images if any */}
        {Array.isArray(blogData.images) &&
          blogData.images.map((image, index) => (
            <div key={index} className="my-8">
              <img
                src={image.url}
                alt={image.alt || `Blog image ${index + 1}`}
                className="w-full rounded-lg shadow-lg"
                loading="lazy"
              />
              {image.caption && (
                <p className="mt-2 text-sm text-gray-500 text-center">
                  {image.caption}
                </p>
              )}
            </div>
          ))}
      </article>
    </div>
  );
}
